import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { convertToSnakeCase } from '@/models/utils/casing'
import { useJobFilterStore } from '../JobsFilterStore'
import { dateWindowFlag, mapMaxDateRangeDays, normalizeMapDateRange } from '../date-window'
import { getInstance } from '@/auth'
import moment from 'moment'

interface Feature {
  id: string
  type: 'Feature'
  geometry: {
    type: 'Point'
    coordinates: [number, number]
  }
  properties: {
    LocationId: number
    LocationName: string
    Latitude: number
    Longitude: number
    Pickup: {
      Total: number
      // Dynamic status counts - examples: Active, Completed, Pending, etc.
      [key: string]: number
    }
    Dropoff: {
      Total: number
      // Dynamic status counts - examples: Active, Completed, Pending, etc.
      [key: string]: number
    }
    TotalCount: number
  }
}

interface FeatureCollection {
  type: 'FeatureCollection'
  features: Feature[]
}

const nullIfEmpty = (value?: any[]) => {
  if (value?.length === 0) {
    return null
  }
  return value
}

export function useJobsGeoJSON() {
  const jobFilterStore = useJobFilterStore()
  // Repair old persisted all-dates/oversized filters before the first map request.
  const ensureDateRange = () => {
    if (!jobFilterStore.dateWindowEnabled || jobFilterStore.maxDateRangeDays === null) return
    jobFilterStore.rollDefaultDateRange()
    const range = normalizeMapDateRange(
      jobFilterStore.start,
      jobFilterStore.end,
      jobFilterStore.maxDateRangeDays,
    )
    if (!range) {
      jobFilterStore.resetDateRange()
    } else if (range.start !== jobFilterStore.start || range.end !== jobFilterStore.end) {
      jobFilterStore.$patch(range)
    }
  }
  ensureDateRange()
  // Flags load after the app renders, so apply the window once the flag arrives.
  watch(
    () => [
      jobFilterStore.start,
      jobFilterStore.end,
      jobFilterStore.dateWindowEnabled,
      jobFilterStore.maxDateRangeDays,
    ],
    ensureDateRange,
  )
  // The map stays open across midnight on dispatch screens; keep the default window current.
  let rollTimer: ReturnType<typeof setInterval> | undefined
  const lastModified = ref<Date | null>(null)
  const queryClient = useQueryClient()
  const isComponentMounted = ref(false)
  // Hold the first request until the flag (and, with it on, the cap) has loaded, so it is already in range.
  const canFetch = computed(
    () =>
      isComponentMounted.value &&
      dateWindowFlag.value !== null &&
      (!dateWindowFlag.value || mapMaxDateRangeDays.value !== null),
  )

  onMounted(() => {
    isComponentMounted.value = true
    rollTimer = setInterval(jobFilterStore.rollDefaultDateRange, 60_000)
  })

  onBeforeUnmount(() => {
    isComponentMounted.value = false
    clearInterval(rollTimer)
  })

  const fetchJobsGeoJSON = async (
    isIncremental = false,
    signal?: AbortSignal,
  ): Promise<FeatureCollection> => {
    // The map's filter panel is JobsFilterAside, which edits the job list's filters, not the fieldView* ones.
    const body = convertToSnakeCase({
      // The server classifies past/future windows by the start's local calendar date.
      start: jobFilterStore.start?.toISOString(true) ?? null,
      end: jobFilterStore.end?.toISOString(true) ?? null,
      divisions: nullIfEmpty(jobFilterStore.selectedDivisions),
      serviceProviders: nullIfEmpty(jobFilterStore.selectedServiceProviders),
      statuses: nullIfEmpty(jobFilterStore.selectedJobStatuses),
      approvalStatuses: nullIfEmpty(jobFilterStore.selectedApprovalStatuses),
      resourceTypes: nullIfEmpty(jobFilterStore.selectedResourceTypes),
      activities: nullIfEmpty(jobFilterStore.selectedActivities),
      priorities: nullIfEmpty(jobFilterStore.selectedPriorities),
      locations: nullIfEmpty(jobFilterStore.selectedLocations),
      batchStatuses: nullIfEmpty(jobFilterStore.selectedBatchStatuses),
      modifiedSince: isIncremental
        ? moment(lastModified.value).subtract(10, 's').toISOString()
        : null,
    })

    const authService = getInstance()
    const token = await authService.getTokenSilently()

    const response = await fetch('/api/v3/field-view/geojson/jobs', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal,
    })

    if (!response.ok) {
      throw new Error('Failed to fetch jobs GeoJSON')
    }

    const result = await response.json()

    // Update last modified timestamp
    if (!isIncremental) {
      lastModified.value = new Date()
    }

    return result
  }

  const mergeFeatureCollections = (
    existing: FeatureCollection,
    updates: FeatureCollection,
  ): FeatureCollection => {
    if (!existing || !existing.features) {
      return updates
    }

    // Create a map of existing features by LocationId for efficient lookup
    const existingFeatureMap = new Map(
      existing.features.map((feature) => [feature.properties.LocationId, feature]),
    )

    // Update or add features from the updates; a zero count means no job on the map is there any more
    updates.features.forEach((updatedFeature) => {
      if (updatedFeature.properties.TotalCount === 0) {
        existingFeatureMap.delete(updatedFeature.properties.LocationId)
      } else {
        existingFeatureMap.set(updatedFeature.properties.LocationId, updatedFeature)
      }
    })

    return {
      type: 'FeatureCollection',
      features: Array.from(existingFeatureMap.values()),
    }
  }

  // Create a computed query key that includes all filter dependencies
  const queryKey = computed(() => [
    'jobsGeoJSON',
    {
      start: jobFilterStore.start,
      end: jobFilterStore.end,
      divisions: jobFilterStore.selectedDivisions,
      serviceProviders: jobFilterStore.selectedServiceProviders,
      statuses: jobFilterStore.selectedJobStatuses,
      approvalStatuses: jobFilterStore.selectedApprovalStatuses,
      resourceTypes: jobFilterStore.selectedResourceTypes,
      activities: jobFilterStore.selectedActivities,
      priorities: jobFilterStore.selectedPriorities,
      locations: jobFilterStore.selectedLocations,
      batchStatuses: jobFilterStore.selectedBatchStatuses,
    },
  ])

  // A different filter set needs a full result, even if the previous window was polled.
  watch(
    queryKey,
    () => {
      lastModified.value = null
    },
    { deep: true, flush: 'sync' },
  )

  const {
    data: geoJsonData,
    refetch,
    isLoading,
    error,
    isRefetching,
  } = useQuery({
    queryKey,
    placeholderData: {
      type: 'FeatureCollection',
      features: [],
    } as FeatureCollection,
    queryFn: async (context) => {
      // Check if component is still mounted before proceeding
      if (!isComponentMounted.value) {
        throw new Error('Component unmounted')
      }

      const isIncremental = lastModified.value !== null
      const result = await fetchJobsGeoJSON(isIncremental, context.signal)

      // Check again after async operation
      if (!isComponentMounted.value) {
        throw new Error('Component unmounted during fetch')
      }

      if (isIncremental) {
        const previousData = queryClient.getQueryData<FeatureCollection>(context.queryKey)
        if (previousData) {
          const mergedData = mergeFeatureCollections(previousData, result)
          lastModified.value = new Date()
          return mergedData
        }
      }

      if (!isIncremental) {
        lastModified.value = new Date()
      }
      return result
    },
    refetchOnReconnect: false,
    keepPreviousData: true,
    refetchInterval: () => {
      // Only refetch if component is still mounted
      return isComponentMounted.value ? 30000 : false
    },
    staleTime: 0,
    enabled: canFetch,
  })

  // Function to reset incremental state and force full reload
  const resetAndRefetch = async () => {
    if (!canFetch.value) {
      return Promise.resolve()
    }

    lastModified.value = null
    return refetch()
  }

  return {
    geoJsonData,
    refetch,
    resetAndRefetch,
    isLoading,
    error,
    isRefetching,
    lastModified,
    isComponentMounted,
  }
}

export type { Feature, FeatureCollection }
